// React, Next
import { useContext } from 'react'
import Image, { StaticImageData } from 'next/image'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export type PillarItem = {
  title: string
  paragraphs: string[]
  /** Only some items lead somewhere; the rest are description only. */
  link?: { label: string; href: string }
}

type Props = {
  eyebrow: string
  title: string
  paragraphs: string[]
  image: StaticImageData
  /** Which side the photo sits on, so consecutive pillars alternate. */
  imageSide?: 'left' | 'right'
  items: PillarItem[]
  background?: string
  /** Cards need to contrast with whatever band the section sits on. */
  cardBackground?: string
}

/**
 * The two pillars of the homepage share a shape: a headline block beside a
 * photo, then a row of cards underneath.
 */
export default function PillarSection(props: Props) {
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const stacked = w < breakpoints.desktopToMobile

  const getCardGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 26rem)',
          gap: stacked ? '2rem' : '3rem',
          alignItems: 'center',
          marginBottom: '3rem'
        }}
      >
        <Box sx={{ order: props.imageSide === 'left' && !stacked ? 2 : 1 }}>
          <SectionHeader
            eyebrow={props.eyebrow}
            title={props.title}
            intro={props.paragraphs}
          />
        </Box>

        <Box
          sx={{
            order: props.imageSide === 'left' && !stacked ? 1 : 2,
            lineHeight: 0,
            borderRadius: '10px',
            overflow: 'hidden'
          }}
        >
          <Image
            src={props.image}
            alt=""
            sizes="(max-width: 990px) 100vw, 40vw"
            // Uncropped, and never scaled past its own pixel width.
            style={{
              width: '100%',
              height: 'auto',
              maxWidth: props.image.width
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getCardGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {props.items.map(item => (
          <Box
            key={item.title}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              background:
                props.cardBackground ?? themeColors.kroemtech.background,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.75rem',
              boxSizing: 'border-box'
            }}
          >
            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: themeColors.gray.dark,
                marginBottom: '0.9rem'
              }}
            >
              {item.title}
            </Typography>

            {item.paragraphs.map(paragraph => (
              <Typography
                key={paragraph}
                component="p"
                sx={{
                  fontSize: getFontBodySmall(),
                  lineHeight: 1.7,
                  color: themeColors.gray.defaultText,
                  marginBottom: '0.8rem',
                  '&:last-of-type': { marginBottom: 0 }
                }}
              >
                {paragraph}
              </Typography>
            ))}

            {item.link && (
              <Box
                component={Link}
                href={item.link.href}
                sx={{
                  // Pinned to the bottom so the links line up across cards.
                  marginTop: 'auto',
                  paddingTop: '1.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: getFontBodySmall(),
                  fontWeight: 600,
                  color: themeColors.success.darker,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {item.link.label}
                <ArrowForwardIosIcon sx={{ fontSize: '0.7rem' }} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={props.background ?? themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
